import React, { useMemo } from "react";
import i18n from "@/utils/i18n";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type { DashboardCategorySuccessCompletion } from "../../types/dashboard.types";
import styles from "./CategoryPerformanceChart.module.scss";

interface CategoryPerformanceChartProps {
  title: string;
  data: DashboardCategorySuccessCompletion | null;
  seriesLabels?: { success: string; completion: string };
}

const CategoryPerformanceChart: React.FC<CategoryPerformanceChartProps> = ({
  title,
  data,
  seriesLabels,
}) => {
  const items = data?.items ?? [];
  const categories = useMemo(() => items.map((i) => i.label), [items]);
  const successSeries = useMemo(
    () => items.map((i) => Math.round(i.successPercent ?? 0)),
    [items],
  );
  const completionSeries = useMemo(
    () => items.map((i) => Math.round(i.completionPercent ?? 0)),
    [items],
  );

  const avg = useMemo(() => {
    if (items.length === 0) return { success: 0, completion: 0 };
    const sumS = successSeries.reduce((a, b) => a + b, 0);
    const sumC = completionSeries.reduce((a, b) => a + b, 0);
    return {
      success: Math.round(sumS / items.length),
      completion: Math.round(sumC / items.length),
    };
  }, [items.length, successSeries, completionSeries]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        fontFamily: "Inter, sans-serif",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: {
          enabled: true,
          speed: 550,
          animateGradually: { enabled: true, delay: 120 },
          dynamicAnimation: { enabled: true, speed: 400 },
        },
        dropShadow: {
          enabled: true,
          top: 6,
          left: 0,
          blur: 14,
          color: "#0f172a",
          opacity: 0.1,
        },
      },
      colors: ["#8b5cf6", "#06b6d4"],
      stroke: {
        curve: "smooth",
        width: 3,
        lineCap: "round",
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.55,
          opacityTo: 0.05,
          stops: [0, 100],
          colorStops: [
            [
              { offset: 0, color: "#a78bfa", opacity: 0.55 },
              { offset: 100, color: "#a78bfa", opacity: 0.04 },
            ],
            [
              { offset: 0, color: "#22d3ee", opacity: 0.45 },
              { offset: 100, color: "#22d3ee", opacity: 0.03 },
            ],
          ],
        },
      },
      markers: {
        size: 0,
        colors: ["#ffffff"],
        strokeColors: ["#8b5cf6", "#06b6d4"],
        strokeWidth: 3,
        hover: { size: 7 },
      },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: "#707a8f",
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 4,
        labels: {
          formatter: (val: number) => `%${Math.round(val)}`,
          style: {
            colors: "#707a8f",
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
      grid: {
        borderColor: "#eef0f3",
        strokeDashArray: 6,
        padding: { left: 8, right: 8, top: 0, bottom: 0 },
        xaxis: { lines: { show: false } },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 500,
        labels: { colors: "#2f333c" },
        markers: {
          width: 10,
          height: 10,
          radius: 10,
          offsetX: -4,
        },
        itemMargin: { horizontal: 12 },
      },
      tooltip: {
        theme: "light",
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number) => `%${val}`,
        },
      },
      dataLabels: { enabled: false },
    }),
    [categories],
  );

  const series = useMemo(
    () => [
      {
        name: seriesLabels?.success ?? i18n.t("categoryPerformance.success"),
        data: successSeries,
      },
      {
        name: seriesLabels?.completion ?? "Tamamlanma",
        data: completionSeries,
      },
    ],
    [seriesLabels, successSeries, completionSeries],
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.chips}>
          <div className={`${styles.chip} ${styles.chipSuccess}`}>
            <span className={styles.chipLabel}>
              {seriesLabels?.success ?? i18n.t("categoryPerformance.success")} ort.
            </span>
            <span className={styles.chipValue}>%{avg.success}</span>
          </div>
          <div className={`${styles.chip} ${styles.chipCompletion}`}>
            <span className={styles.chipLabel}>
              {seriesLabels?.completion ?? "Tamamlanma"} ort.
            </span>
            <span className={styles.chipValue}>%{avg.completion}</span>
          </div>
        </div>
      </div>

      <div className={styles.chartWrap}>
        {items.length > 0 ? (
          <Chart
            options={options}
            series={series}
            type="area"
            height={300}
          />
        ) : (
          <div className={styles.empty}>Veri yok</div>
        )}
      </div>
    </div>
  );
};

export default CategoryPerformanceChart;
